import MiniSearch, { Options, SearchOptions, SearchResult } from "minisearch"
import { useMemo, useRef, useState } from "react"
import { useDebounce } from "react-use"

// Normalize data types into a common shape for search indexing
// Each indexable item should have: id: string, title: string, description?: string, content?: string, parentPath?: string

export type SearchIndex = typeof MiniSearch

// For any item to be converted to an Indexable item, it must implement the SearchableItem interface
export interface SearchableItem<
  I extends string | number = string | number,
  T = any
> {
  id: I
  type: T
}

export type IndexableItem = SearchableItem & {
  title: string
  description?: string
  content?: string
  parentPath?: string
}

export type SearchResultItem = IndexableItem | SearchResult

export function createIndex(items: IndexableItem[], options = {}) {
  const miniSearch = new MiniSearch({
    fields: ["title", "description", "content"],
    storeFields: ["id", "title", "description", "content", "type"],
    ...options
  })

  miniSearch.addAll(items)
  return miniSearch
}

const TITLE_SNIPPET_LENGTH = 30
const CONTENT_SNIPPET_LENGTH = 70

const getSnippet = (
  value: string,
  matchStart: number,
  matchEnd: number,
  field: string
): string => {
  const snippetLength =
    field === "title" ? TITLE_SNIPPET_LENGTH : CONTENT_SNIPPET_LENGTH

  const halfSnippet = Math.floor(snippetLength / 2)
  const snippetStart = Math.max(0, matchStart - halfSnippet)
  const snippetEnd = Math.min(value.length, matchEnd + halfSnippet)
  let snippet = value.slice(snippetStart, snippetEnd)

  if (snippetStart > 0) snippet = "..." + snippet
  if (snippetEnd < value.length) snippet += "..."

  return snippet
}

function markSnippets(result: SearchResult) {
  const snippets: Record<string, string> = {}

  const fields = new Set<string>()

  // Collect all fields that contain matches
  result.terms.forEach((term) => {
    result.match[term]?.forEach((field) => {
      fields.add(field)
    })
  })

  for (const field of fields) {
    const value = result[field]
    if (typeof value !== "string") continue

    const lowerValue = value.toLowerCase()
    const ranges: { start: number; end: number }[] = []

    // Collect all match ranges
    result.terms.forEach((term) => {
      const matchIndex = lowerValue.indexOf(term.toLowerCase())
      if (matchIndex !== -1) {
        ranges.push({
          start: matchIndex,
          end: matchIndex + term.length
        })
      }
    })

    // Sort and merge adjacent/overlapping ranges
    ranges.sort((a, b) => a.start - b.start)
    const merged: typeof ranges = []
    for (const range of ranges) {
      const last = merged[merged.length - 1]
      if (last && range.start <= last.end) {
        last.end = Math.max(last.end, range.end)
      } else {
        merged.push({ ...range })
      }
    }

    // Determine snippet boundaries
    const snippetLength =
      field === "title" ? TITLE_SNIPPET_LENGTH : CONTENT_SNIPPET_LENGTH

    const first = merged[0]
    const last = merged[merged.length - 1]
    const snippetStart = Math.max(0, first.start - Math.floor(snippetLength / 2))
    const snippetEnd = Math.min(value.length, last.end + Math.floor(snippetLength / 2))
    const snippetText = value.slice(snippetStart, snippetEnd)

    // Mark all merged ranges in the snippet
    let marked = ""
    let cursor = snippetStart

    for (const { start, end } of merged) {
      const safeStart = Math.max(start, snippetStart)
      const safeEnd = Math.min(end, snippetEnd)

      if (cursor < safeStart) {
        marked += value.slice(cursor, safeStart)
      }
      marked += `<mark>${value.slice(safeStart, safeEnd)}</mark>`
      cursor = safeEnd
    }

    if (cursor < snippetEnd) {
      marked += value.slice(cursor, snippetEnd)
    }

    snippets[field] =
      (snippetStart > 0 ? "..." : "") +
      marked +
      (snippetEnd < value.length ? "..." : "")
  }

  return snippets
}

type CustomOptions = {
  returnOriginalItems?: boolean
  includeSnippets?: boolean
  minimumQueryLength?: number
}

export type NormalizedSearchOptions = {
  searchOptions: SearchOptions
  indexOptions: Partial<Options>
} & CustomOptions

function normalizeOptions(
  options: NormalizedSearchOptions
): Required<NormalizedSearchOptions> {
  const {
    returnOriginalItems = true,
    includeSnippets = true,
    minimumQueryLength = 2,
    ...rest
  } = options

  return {
    returnOriginalItems,
    includeSnippets,
    minimumQueryLength,
    ...rest
  }
}

export function useNormalizedSearch(
  items: IndexableItem[],
  query: string = "",
  options = {} as NormalizedSearchOptions
): SearchResultItem[] {
  const normalizedOptions: Required<NormalizedSearchOptions> = useMemo(
    () => normalizeOptions(options),
    [options]
  )

  const {
    returnOriginalItems,
    includeSnippets,
    minimumQueryLength,
    indexOptions,
    searchOptions
  } = normalizedOptions

  const searchIndex = useMemo(() => {
    try {
      return createIndex(items, indexOptions as Partial<Options>)
    } catch (e) {
      console.error("Error creating search index:", e)
      return createIndex([], indexOptions as Partial<Options>)
    }
  }, [items, indexOptions])

  const [debouncedSearch, setDebouncedQuery] = useState(query)
  useDebounce(() => setDebouncedQuery(query), 300, [query])

  const previousResultRef = useRef<SearchResultItem[]>([])

  return useMemo(() => {
    // If there's a search query and it's shorter than the minimum query length, return an empty array
    if (debouncedSearch && debouncedSearch.length < minimumQueryLength) {
      return []
    }

    // If the search query is empty and we want to return the original items, return them
    if (!debouncedSearch && returnOriginalItems) {
      return items
    }

    try {
      const searched = searchIndex
        .search(debouncedSearch, searchOptions)
        .map((result) => {
          if (includeSnippets) {
            return {
              ...result,
              snippets: markSnippets(result)
            }
          }
          return result
        })

      if (Object.is(previousResultRef.current, searched)) {
        return previousResultRef.current
      }

      previousResultRef.current = searched
      return searched
    } catch (e) {
      console.error("Search error:", e)
      return []
    }
  }, [searchIndex, debouncedSearch, items])
}
